import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Play, RotateCcw, StopCircle, Info, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

function Row({ label, value, mono, testid }) {
    return (
        <div className="flex items-baseline justify-between py-2 border-b border-[#27272A] last:border-0">
            <span className="text-[13px] uppercase tracking-wider text-white/55">{label}</span>
            <span
                data-testid={testid}
                className={`text-sm ${mono ? "font-mono" : "font-semibold"} text-white`}
            >
                {value ?? "—"}
            </span>
        </div>
    );
}

function StatusPill({ status }) {
    const map = {
        idle: "bg-white/10 text-white/70 border-white/15",
        running: "bg-[#00BCE5]/15 text-[#00BCE5] border-[#00BCE5]/30",
        completed: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
        cancelled: "bg-amber-500/15 text-amber-400 border-amber-500/30",
        failed: "bg-red-500/15 text-red-400 border-red-500/30",
    };
    const cls = map[status] || map.idle;
    return (
        <span
            data-testid="backfill-status-pill"
            className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold px-2.5 py-1 rounded border ${cls}`}
        >
            {status === "running" && <Loader2 className="h-3 w-3 animate-spin" />}
            {status === "completed" && <CheckCircle2 className="h-3 w-3" />}
            {status === "failed" && <AlertTriangle className="h-3 w-3" />}
            {status || "idle"}
        </span>
    );
}

export default function AdminMaintenance() {
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [starting, setStarting] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const pollRef = useRef(null);

    const fetchStatus = async () => {
        try {
            const { data } = await api.get("/admin/maintenance/image-backfill/status");
            setJob(data);
        } catch (e) {
            // Silent on poll errors; surface only on explicit clicks.
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStatus();
        pollRef.current = setInterval(fetchStatus, 2000);
        return () => pollRef.current && clearInterval(pollRef.current);
    }, []);

    const onStart = async () => {
        setStarting(true);
        try {
            const { data } = await api.post("/admin/maintenance/image-backfill/start");
            setJob(data.job);
            if (data.started) {
                toast.success("Backfill started. Progress will update automatically.");
            } else {
                toast.info("A backfill is already running.");
            }
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Could not start backfill.");
        } finally {
            setStarting(false);
        }
    };

    const onCancel = async () => {
        setCancelling(true);
        try {
            const { data } = await api.post("/admin/maintenance/image-backfill/cancel");
            setJob(data.job);
            toast("Cancel requested — current image will finish then the job stops.");
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Could not cancel.");
        } finally {
            setCancelling(false);
        }
    };

    const onForceReset = async () => {
        if (!window.confirm(
            "Force-reset the backfill job state to idle?\n\n" +
            "This abandons any stuck task on the server and clears the job doc.\n" +
            "Originals and already-generated variants are NOT touched.\n" +
            "Use only when the job appears stuck in `running` and Cancel does not respond."
        )) return;
        try {
            const { data } = await api.post("/admin/maintenance/image-backfill/force-reset");
            await fetchStatus();
            toast.success(data?.message || "Job state reset to idle.");
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Could not reset.");
        }
    };

    if (loading) return <div className="text-white/55">Loading…</div>;

    const status = job?.status || "idle";
    const isRunning = status === "running";
    const pct = job?.percent ?? 0;

    return (
        <div data-testid="admin-maintenance-page" className="space-y-6 max-w-3xl">
            <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-white/45">Operations</div>
                <h1 className="text-4xl font-bold tracking-tight text-white">Maintenance</h1>
                <p className="mt-2 text-sm text-white/60">
                    Admin-only one-off jobs. Runs in the background; safe to close this page and come back.
                </p>
            </div>

            <div className="bg-[#121214] border border-[#27272A] rounded-lg p-6">
                <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                        <h2 className="text-xl font-bold text-white">Image Optimization Backfill</h2>
                        <p className="mt-1 text-sm text-white/60">
                            Generates WebP <span className="font-mono text-white/80">large / medium / thumbnail</span>{" "}
                            variants for legacy product images. Idempotent. Originals are never modified. Broken or
                            unsupported images are marked once so they don’t block the queue.
                        </p>
                    </div>
                    <StatusPill status={status} />
                </div>

                <div className="mt-5 flex flex-wrap items-center gap-2">
                    <button
                        data-testid="backfill-start-btn"
                        onClick={onStart}
                        disabled={starting || isRunning}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded bg-[#00BCE5] text-black font-semibold hover:bg-[#00a7cc] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                        {isRunning ? "Running…" : "Run Image Optimization Backfill"}
                    </button>
                    <button
                        data-testid="backfill-cancel-btn"
                        onClick={onCancel}
                        disabled={cancelling || !isRunning}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded border border-[#27272A] text-white/80 hover:bg-[#1a1a1e] disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <StopCircle className="h-4 w-4" /> Cancel
                    </button>
                    <button
                        data-testid="backfill-refresh-btn"
                        onClick={fetchStatus}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded border border-[#27272A] text-white/60 hover:text-white hover:bg-[#1a1a1e]"
                    >
                        <RotateCcw className="h-4 w-4" /> Refresh
                    </button>
                    <button
                        data-testid="backfill-force-reset-btn"
                        onClick={onForceReset}
                        className="ml-auto inline-flex items-center gap-2 px-3 py-2 rounded border border-amber-500/30 text-amber-400 text-xs hover:bg-amber-500/10"
                        title="Hard-reset the job state. Use only when the job appears stuck and Cancel does not respond."
                    >
                        <AlertTriangle className="h-3.5 w-3.5" /> Force reset
                    </button>
                </div>

                {/* Progress bar */}
                {(isRunning || status === "completed" || status === "cancelled") && (
                    <div className="mt-6">
                        <div className="flex items-center justify-between text-xs text-white/60 mb-1.5">
                            <span>Progress</span>
                            <span data-testid="backfill-percent" className="font-mono">{pct}%</span>
                        </div>
                        <div className="h-2 w-full bg-[#1a1a1e] rounded overflow-hidden">
                            <div
                                data-testid="backfill-progress-bar"
                                className="h-full bg-[#00BCE5] transition-all"
                                style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
                            />
                        </div>
                    </div>
                )}

                {/* Stats grid */}
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-x-8">
                    <div>
                        <Row label="Status" value={status} testid="job-field-status" />
                        <Row label="Total pending (start)" value={job?.total_pending_at_start ?? 0} mono testid="job-field-total-pending" />
                        <Row label="Pending remaining" value={job?.pending_remaining ?? 0} mono testid="job-field-pending-remaining" />
                        <Row label="Processed" value={job?.processed ?? 0} mono testid="job-field-processed" />
                    </div>
                    <div>
                        <Row label="Generated" value={job?.generated ?? 0} mono testid="job-field-generated" />
                        <Row label="Marked unsupported" value={job?.marked_unsupported ?? 0} mono testid="job-field-unsupported" />
                        <Row label="Failed" value={job?.failed ?? 0} mono testid="job-field-failed" />
                        <Row
                            label="Current image"
                            value={
                                job?.current_image_id
                                    ? <span className="font-mono text-xs">{job.current_image_id.slice(0, 8)}…</span>
                                    : "—"
                            }
                            testid="job-field-current-image"
                        />
                    </div>
                </div>

                {/* Live activity */}
                {(job?.current_status || job?.current_reason || typeof job?.seconds_since_heartbeat === "number") && (
                    <div className="mt-5 p-3 rounded border border-[#27272A] bg-black/30 text-xs">
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                            {job?.current_status && (
                                <span data-testid="job-field-current-status">
                                    <span className="text-white/45 uppercase tracking-wider mr-1.5">Last action</span>
                                    <span className="font-semibold text-white">{job.current_status}</span>
                                </span>
                            )}
                            {typeof job?.seconds_since_heartbeat === "number" && (
                                <span data-testid="job-field-heartbeat">
                                    <span className="text-white/45 uppercase tracking-wider mr-1.5">Heartbeat</span>
                                    <span
                                        className={`font-mono ${
                                            isRunning && job.seconds_since_heartbeat > 60
                                                ? "text-amber-400"
                                                : "text-white/80"
                                        }`}
                                    >
                                        {job.seconds_since_heartbeat}s ago
                                    </span>
                                </span>
                            )}
                            {typeof job?.per_image_timeout_s === "number" && (
                                <span>
                                    <span className="text-white/45 uppercase tracking-wider mr-1.5">Per-image timeout</span>
                                    <span className="font-mono text-white/80">{job.per_image_timeout_s}s</span>
                                </span>
                            )}
                        </div>
                        {job?.current_reason && (
                            <div className="mt-1.5 text-white/60">
                                <span className="text-white/45 uppercase tracking-wider mr-1.5">Reason</span>
                                <span data-testid="job-field-current-reason" className="font-mono break-words">{job.current_reason}</span>
                            </div>
                        )}
                    </div>
                )}

                {job?.last_error && (
                    <div className="mt-5 p-3 rounded border border-red-500/30 bg-red-500/10 text-xs text-red-300">
                        <div className="font-semibold mb-1">Last error</div>
                        <div className="font-mono break-words">{job.last_error}</div>
                    </div>
                )}

                <div className="mt-5 flex items-start gap-2 text-xs text-white/45">
                    <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                    <span>
                        Safe to re-run anytime. Skips images that are already optimized or flagged unsupported. The job
                        survives navigation — this page auto-refreshes every 2 seconds while it runs.
                    </span>
                </div>

                {job?.backfill_version && (
                    <div className="mt-3 text-[10px] uppercase tracking-[0.18em] text-white/30 font-mono" data-testid="backfill-version">
                        backend build: {job.backfill_version}
                    </div>
                )}
            </div>
        </div>
    );
}
